import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send, Sparkles, User } from "lucide-react";
import { PHONE_RAW } from "@/lib/api";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const QUICK_PROMPTS = ["Book a blood test", "Home sample collection", "Download my report", "Package prices"];

const GREETINGS = {
  en: "Hi! I'm Hema, Hemozy's care assistant. Ask me about tests, prices, home collection, bookings or reports.",
  te: "నమస్కారం! నేను హేమ, Hemozy కేర్ అసిస్టెంట్. టెస్టులు, ధరలు, హోమ్ కలెక్షన్, బుకింగ్ లేదా రిపోర్టుల గురించి అడగండి.",
  hi: "नमस्ते! मैं हेमा हूं, Hemozy की केयर असिस्टेंट। टेस्ट, कीमत, होम कलेक्शन, बुकिंग या रिपोर्ट के बारे में पूछें।",
};

const LANGS = [
  { id: "en", label: "EN" },
  { id: "te", label: "తెలుగు" },
  { id: "hi", label: "हिंदी" },
];

function getSessionId() {
  let id = localStorage.getItem("hema_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("hema_session", id);
  }
  return id;
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState("en");
  const [messages, setMessages] = useState([{ role: "assistant", text: GREETINGS.en }]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const bottomRef = useRef(null);
  const sessionId = useRef(getSessionId());

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || streaming) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }, { role: "assistant", text: "" }]);
    setStreaming(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: msg, language: lang }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n\n");
        buf = lines.pop();
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") break;
          try {
            const { c } = JSON.parse(payload);
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + c };
              return copy;
            });
          } catch {}
        }
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: "I'm having trouble connecting. Please WhatsApp us at +91 97766 15544." };
        return copy;
      });
    } finally {
      setStreaming(false);
    }
  };

  const switchLang = (id) => {
    setLang(id);
    setMessages((m) => [...m, { role: "assistant", text: GREETINGS[id] }]);
  };

  return (
    <>
      {/* Floating button */}
      <motion.button onClick={() => setOpen(!open)} data-testid="hema-chat-button"
        initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 1.5, type: "spring", stiffness: 200, damping: 14 }}
        className="fixed bottom-5 right-5 z-[90] w-14 h-14 rounded-full bg-[#D92638] hover:bg-[#B81D2D] text-white shadow-[0_10px_35px_-8px_rgba(217,38,56,0.6)] flex items-center justify-center transition-all duration-200 hover:-translate-y-1 active:scale-90"
        aria-label="Chat with Hema">
        <AnimatePresence mode="wait">
          {open ? <X key="x" className="w-6 h-6" /> : <MessageCircle key="c" className="w-6 h-6" />}
        </AnimatePresence>
        {!open && <span className="absolute -top-1 -right-1 w-3.5 h-3.5 rounded-full bg-[#14B8A6] border-2 border-white animate-pulse" />}
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: 30, scale: 0.96 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 30, scale: 0.96 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
            className="fixed bottom-[84px] right-5 z-[90] w-[calc(100vw-40px)] max-w-sm bg-white rounded-3xl shadow-[0_30px_80px_-15px_rgba(11,25,44,0.45)] border border-slate-200 overflow-hidden flex flex-col"
            data-testid="hema-chat-panel" style={{ height: "min(560px, 70vh)" }}>
            {/* Header */}
            <div className="bg-[#0B192C] px-5 py-4 flex items-center gap-3.5">
              <div className="w-10 h-10 rounded-full bg-[#D92638]/20 border border-[#D92638]/40 flex items-center justify-center shrink-0">
                <Sparkles className="w-5 h-5 text-[#D92638]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white font-bold text-sm" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>Hema</div>
                <div className="flex items-center gap-1.5 text-[11px] text-slate-400 whitespace-nowrap">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#14B8A6]" /> Care Assistant · online
                </div>
              </div>
              <div className="flex items-center gap-1 bg-white/10 rounded-full p-1" data-testid="hema-lang-toggle">
                {LANGS.map((l) => (
                  <button key={l.id} onClick={() => switchLang(l.id)} data-testid={`hema-lang-${l.id}`}
                    className={`text-[10px] font-bold rounded-full px-2.5 py-1 transition-all active:scale-95 ${lang === l.id ? "bg-[#D92638] text-white" : "text-slate-300 hover:text-white"}`}>
                    {l.label}
                  </button>
                ))}
              </div>
              <a href={`https://wa.me/${PHONE_RAW}?text=${encodeURIComponent("Hi Hemozy! I need help.")}`} target="_blank" rel="noreferrer"
                className="text-[10px] font-bold text-[#14B8A6] border border-[#14B8A6]/40 rounded-full px-3 py-1.5 hover:bg-[#14B8A6] hover:text-[#0B192C] transition-colors"
                data-testid="hema-escalate-whatsapp">
                Talk to human
              </a>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-[#F8FAFC]" data-testid="hema-messages">
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  {m.role === "assistant" && (
                    <div className="w-7 h-7 rounded-full bg-[#0B192C] flex items-center justify-center shrink-0 mt-0.5">
                      <Sparkles className="w-3.5 h-3.5 text-[#D92638]" />
                    </div>
                  )}
                  <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-[#D92638] text-white rounded-br-md" : "bg-white border border-slate-200 text-slate-700 rounded-bl-md shadow-sm"}`}
                    data-testid={`hema-message-${i}`}>
                    {m.text ? m.text.replace(/\*\*/g, "") : (streaming && i === messages.length - 1 ? (
                      <span className="inline-flex gap-1 py-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" />
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0.15s" }} />
                        <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" style={{ animationDelay: "0.3s" }} />
                      </span>
                    ) : "")}
                  </div>
                  {m.role === "user" && (
                    <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center shrink-0 mt-0.5">
                      <User className="w-3.5 h-3.5 text-slate-500" />
                    </div>
                  )}
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {/* Quick prompts */}
            {messages.length <= 1 && (
              <div className="px-4 py-2.5 bg-[#F8FAFC] border-t border-slate-100 flex gap-2 overflow-x-auto">
                {QUICK_PROMPTS.map((q) => (
                  <button key={q} onClick={() => send(q)} data-testid={`hema-quick-${q.replace(/\s+/g, "-").toLowerCase()}`}
                    className="shrink-0 text-xs font-medium text-[#0D9488] border border-[#0D9488]/40 rounded-full px-3.5 py-1.5 hover:bg-[#0D9488] hover:text-white transition-colors active:scale-95">
                    {q}
                  </button>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="p-3.5 bg-white border-t border-slate-100 flex items-center gap-2.5">
              <input value={input} onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && send()}
                placeholder="Ask Hema anything..." data-testid="hema-input"
                className="flex-1 rounded-full border border-slate-200 px-4.5 px-5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#0D9488]/40 focus:border-[#0D9488]" />
              <button onClick={() => send()} disabled={streaming || !input.trim()} data-testid="hema-send-button"
                className="w-10 h-10 rounded-full bg-[#D92638] hover:bg-[#B81D2D] text-white flex items-center justify-center transition-all active:scale-90 disabled:opacity-40 shrink-0"
                aria-label="Send">
                <Send className="w-4 h-4" />
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
